/** @jsx jsx */
import { jsx } from '@emotion/react';

import useRouter from 'js/lib/useRouter';

import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import PillGroup from 'bundles/browse-common/components/Pill/PillGroup';
import SectionHeadingComponent from 'bundles/browse-common/components/SectionHeadingComponent';
import TrackedPill from 'bundles/browse-common/components/TrackedPill';
import { usePageConfigComponentTrackingData } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';

type Skill = {
  id: string;
  skillName: string;
};

type Props = {
  title: string;
  skills: Skill[];
  showPageTitleHeadingSize?: boolean;
};

const getTrackingData = (url: string, pageConfigComponentTrackingData?: PageConfigComponentTrackingData) => {
  return {
    button: {
      name: 'browse_related_skill' as const,
      linkType: 'internal' as const,
      linkURL: url,
      ...(pageConfigComponentTrackingData && { pageConfigComponent: pageConfigComponentTrackingData }),
    },
  };
};

const RelatedSkillsComponent = (props: Props) => {
  const { title, skills, showPageTitleHeadingSize = false } = props;
  const track = useTracker();
  const isConsumerSerp = useIsConsumerSerp();
  const router = useRouter();
  const pageConfigComponentTrackingData = usePageConfigComponentTrackingData();

  const handleSkillClick = (skill: Skill) => {
    if (isConsumerSerp) {
      const url = `/search?query=${encodeURIComponent(skill.skillName.toLowerCase())}`;
      router.push(url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div data-testid="related-skills">
      <SectionHeadingComponent showPageTitleHeadingSize={showPageTitleHeadingSize}>{title}</SectionHeadingComponent>
      <PillGroup
        shouldCollapse
        dataTestId="skills-pills-group"
        onExpand={() => {
          track('click_button', {
            button: {
              name: 'browse_related_skill_expand',
              ...(pageConfigComponentTrackingData && { pageConfigComponent: pageConfigComponentTrackingData }),
            },
          });
        }}
        onCollapse={() => {
          track('click_button', {
            button: {
              name: 'browse_related_skill_collapse',
              ...(pageConfigComponentTrackingData && { pageConfigComponent: pageConfigComponentTrackingData }),
            },
          });
        }}
      >
        {skills.map((skill) => {
          const url = isConsumerSerp
            ? `/search?query=${encodeURIComponent(skill.skillName.toLowerCase())}`
            : `/courses?query=${encodeURIComponent(skill.skillName.toLowerCase())}`;
          return (
            <TrackedPill
              key={skill.id}
              {...(isConsumerSerp ? { onClick: () => handleSkillClick(skill) } : { href: url })}
              trackingData={getTrackingData(url, pageConfigComponentTrackingData)}
            >
              {skill.skillName}
            </TrackedPill>
          );
        })}
      </PillGroup>
    </div>
  );
};

export default RelatedSkillsComponent;
